import { useRef, useState } from 'react'
import { Printer, Copy, Download } from 'lucide-react'
import Modal from './Modal.jsx'
import { DayGrid, WeekGrid } from './BoardGrid.jsx'
import MonthGrid from './MonthGrid.jsx'
import { useAuth } from '../contexts/AuthContext.jsx'
import { useToast } from './Toast.jsx'
import { formatDate, formatMonth, monthOf } from '../lib/dates.js'

/**
 * Print, copy or save the board.
 *
 * All three outputs are one picture: the same grids the board itself draws, rendered
 * off-screen at a fixed width and rasterised. It used to print the WALL rendering instead —
 * a different layout with different columns — so what came off the printer did not look like
 * what was being pointed at on screen, and it ignored how the board was grouped entirely.
 * The grids are handed `readOnly`, which is what strips the hover chrome, the add buttons and
 * the drag handles; nothing else about them changes.
 *
 * Off-screen at a fixed width rather than captured from what is on screen, because the board
 * may be scrolled, have crews pinned aside, and be at whatever width the window happens to
 * be. None of that should reach the output. Collapsed crews DO reach it, deliberately — a
 * band folded away is a decision about what this board is showing.
 */

// Wide enough that the type rasterises legibly without producing a file too large to email.
const RENDER_W = 1600

/**
 * html2canvas cannot parse `color(srgb ...)` — it throws "unsupported color function" and
 * the whole capture fails.
 *
 * That is not an exotic case here: the wall tints each crew band with color-mix(), and a
 * modern browser resolves color-mix to `color(srgb ...)` at computed-value time. So every
 * board with a coloured crew on it would have failed to print. Rewriting the resolved value
 * to rgba() costs nothing and is exact — color-mix(in srgb, ...) is the only mix used, and
 * srgb components are plain 0..1 floats.
 *
 * Applied generically over the computed styles rather than by overriding the handful of
 * rules that use color-mix today, so a rule added later cannot quietly reintroduce it.
 */
const SRGB_FN = /color\(srgb\s+([\d.]+)\s+([\d.]+)\s+([\d.]+)(?:\s*\/\s*([\d.]+))?\)/g
const toRgba = (value) =>
  value.replace(SRGB_FN, (_m, r, g, b, a) => {
    const c = (n) => Math.round(Math.min(1, Math.max(0, Number(n))) * 255)
    return `rgba(${c(r)}, ${c(g)}, ${c(b)}, ${a === undefined ? 1 : Number(a)})`
  })

const COLOUR_PROPS = [
  'backgroundColor',
  'color',
  'borderTopColor',
  'borderRightColor',
  'borderBottomColor',
  'borderLeftColor',
  'outlineColor',
  'textDecorationColor',
  'boxShadow',
]

function flattenModernColours(root, view) {
  for (const node of [root, ...root.querySelectorAll('*')]) {
    const cs = view.getComputedStyle(node)
    for (const prop of COLOUR_PROPS) {
      const v = cs[prop]
      if (v && v.includes('color(')) node.style[prop] = toRgba(v)
    }
  }
}

/**
 * The machinery, with nothing in it that knows what is being photographed: the dialog, the
 * three outputs, and the off-screen stage the picture is taken from.
 *
 * Its own component because the board is no longer the only thing that prints — a list
 * prints the same three ways, from the same stage, and the fiddly parts here (the
 * blank-page iframe dance, the clipboard capability check, flattening color-mix so
 * html2canvas can read it) are every bit as easy to get wrong for the second caller as for
 * the first. A caller supplies a heading, a file name, and whatever it wants drawn.
 */
export function PrintShell({
  modalTitle = 'Print',
  title,
  subtitle = null,
  fileName,
  stageWidth = RENDER_W,
  onClose,
  children,
}) {
  const { tenant } = useAuth()
  const toast = useToast()
  const stageRef = useRef(null)
  const [busy, setBusy] = useState(null)

  // Whether this browser can put an image on the clipboard at all — Firefox could not at
  // time of writing, and an option that silently does nothing is worse than a disabled one.
  const canCopy =
    typeof window !== 'undefined' &&
    typeof window.ClipboardItem === 'function' &&
    Boolean(navigator.clipboard?.write)

  async function rasterise() {
    // Loaded on demand: a large dependency nobody should pay for just by opening the board.
    // Vite gives it its own chunk.
    const { default: html2canvas } = await import('html2canvas')
    // Measured after layout rather than assumed, so a content-sized stage is captured whole.
    const shot = Math.ceil(stageRef.current.getBoundingClientRect().width) || RENDER_W
    const canvas = await html2canvas(stageRef.current, {
      backgroundColor: '#ffffff',
      // Two device pixels per CSS pixel — legible on paper, still a sane file size.
      scale: 2,
      useCORS: true,
      logging: false,
      width: shot,
      windowWidth: shot,
      onclone: (doc) => {
        const clone = doc.querySelector('[data-print-stage]')
        if (clone) flattenModernColours(clone, doc.defaultView || window)
      },
    })
    return new Promise((resolve, reject) =>
      canvas.toBlob(
        (b) => (b ? resolve(b) : reject(new Error('Could not render the image'))),
        'image/png',
      ),
    )
  }

  async function run(kind, fn) {
    setBusy(kind)
    try {
      await fn(await rasterise())
      if (kind !== 'print') onClose()
    } catch (err) {
      toast.error(err, { fallback: 'That did not work' })
    } finally {
      setBusy(null)
    }
  }

  const toPrinter = (blob) =>
    new Promise((resolve, reject) => {
      const url = URL.createObjectURL(blob)
      // Its own frame rather than printing this page: the app's print stylesheet, the modal
      // and the backdrop are all irrelevant to a picture, and a blank frame has none of them
      // to fight.
      const frame = document.createElement('iframe')
      frame.setAttribute('aria-hidden', 'true')
      frame.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0'

      // srcdoc is set BEFORE the frame is appended, and the handler runs once. Appending an
      // iframe with no source first loads about:blank and fires load against an empty
      // document — which printed a blank page, and only then a second, correct one when the
      // real content arrived.
      frame.srcdoc =
        '<!doctype html><meta charset="utf-8">' +
        '<style>@page{size:landscape;margin:10mm}html,body{margin:0;padding:0}' +
        'img{width:100%;height:auto;display:block}</style>' +
        `<img src="${url}" alt="">`

      let printed = false
      frame.onload = () => {
        const doc = frame.contentDocument
        const img = doc?.images?.[0]
        // Belt and braces against the same blank-page failure: no image in this document
        // means this is not the load we are waiting for.
        if (printed || !img) return
        printed = true

        const go = () => {
          try {
            frame.contentWindow.focus()
            frame.contentWindow.print()
            resolve()
          } catch (err) {
            reject(err)
          } finally {
            // Left in place a while: removing the frame the moment print() returns cancels
            // the job in Safari, which has not taken its copy yet.
            setTimeout(() => {
              frame.remove()
              URL.revokeObjectURL(url)
            }, 60_000)
          }
        }
        // An image that has not decoded yet prints as an empty box.
        if (img.complete) go()
        else {
          img.onload = go
          img.onerror = () => reject(new Error('Could not load the rendered image'))
        }
      }

      document.body.appendChild(frame)
    })

  const toClipboard = async (blob) => {
    await navigator.clipboard.write([new window.ClipboardItem({ 'image/png': blob })])
    toast.success('Copied. Paste it wherever you need it.')
  }

  const toFile = async (blob) => {
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = fileName
    document.body.appendChild(a)
    a.click()
    a.remove()
    setTimeout(() => URL.revokeObjectURL(url), 10_000)
  }

  // A render in flight should not be interrupted half way — the frame and the blob are both
  // mid-construction at that point.
  const close = () => (busy ? undefined : onClose())

  return (
    <Modal title={modalTitle} onClose={close} width={460}>
      <p className="sub">How would you like this view printed?</p>

      <div className="print-choices">
        <button className="print-choice" disabled={Boolean(busy)} onClick={() => run('print', toPrinter)}>
          <Printer size={22} strokeWidth={1.7} />
          <span>{busy === 'print' ? 'Preparing…' : 'Printer'}</span>
        </button>

        <button
          className="print-choice"
          disabled={Boolean(busy) || !canCopy}
          onClick={() => run('copy', toClipboard)}
          title={canCopy ? undefined : 'This browser cannot put an image on the clipboard'}
        >
          <Copy size={22} strokeWidth={1.7} />
          <span>{busy === 'copy' ? 'Copying…' : 'Copy'}</span>
        </button>

        <button className="print-choice" disabled={Boolean(busy)} onClick={() => run('save', toFile)}>
          <Download size={22} strokeWidth={1.7} />
          <span>{busy === 'save' ? 'Saving…' : 'Save'}</span>
        </button>
      </div>

      <div className="modal-actions" style={{ marginTop: 20 }}>
        <button className="btn" type="button" onClick={close} disabled={Boolean(busy)}>
          Close
        </button>
      </div>

      {/* The thing being photographed. Positioned off-screen rather than hidden: html2canvas
          measures a real layout, and display:none or visibility:hidden would hand it a
          zero-sized box. aria-hidden so it is not a second copy of the board for a screen
          reader. */}
      <div className="print-stage" aria-hidden="true">
        <div ref={stageRef} data-print-stage className="print-stage-inner" style={{ width: stageWidth }}>
          <header className="wb-head">
            {tenant?.logoPath && <img src={`/uploads/${tenant.logoPath}`} alt="" />}
            <div className="wb-title">
              <h1>{title}</h1>
              {subtitle && <div className="wb-sub">{subtitle}</div>}
            </div>
          </header>
          {children}
        </div>
      </div>
    </Modal>
  )
}

export default function PrintDialog({ board, crews = null, date, view, onClose }) {
  const { tenant, modes } = useAuth()

  // The same rule the wall applies: a header over people who are on no crew is not what a
  // printed roster is for. See the matching filter in Whiteboard.jsx.
  const printable = {
    ...board,
    groups: (board?.groups || []).filter((g) => g.kind !== 'unassigned'),
  }

  // The month prints as the month it IS, not as the six-week span it draws — the grid runs
  // whole weeks, so board.from/to reach into the neighbouring months and would title an
  // August calendar "27 July to 6 September".
  const month = monthOf(date)
  const title =
    view === 'day'
      ? formatDate(date, { withYear: true })
      : view === 'month'
        ? formatMonth(month)
        : `${formatDate(board.from)} to ${formatDate(board.to, { withYear: true })}`
  const slug = (tenant?.name || 'schedule').toLowerCase().replace(/[^a-z0-9]+/g, '-')
  const stamp = view === 'day' ? date : view === 'month' ? month : `${board.from}_${board.to}`

  return (
    <PrintShell
      modalTitle="Print board"
      title={title}
      subtitle={
        board.mode === 'cat' ? <span className="wb-mode">{modes?.cat?.name || 'Group'}</span> : null
      }
      fileName={`${slug}-${stamp}.png`}
      // The fortnight sizes to its own content instead. Its columns carry a min-width, so
      // fourteen of them plus the staff column come to more than any figure worth
      // hard-coding — pinned at 2400 the table measured 2675 and the last two days were
      // simply cut off the right of the picture. Everything else keeps a fixed width so one
      // day's print and the next look alike.
      stageWidth={view === '2week' ? 'max-content' : RENDER_W}
      onClose={onClose}
    >
      {/* The board's own grids, read-only. `crews` carries the crew colours a people
          board paints its jobs with; on a crew board it is null and the bands carry the
          colour themselves, exactly as on screen. */}
      {view === 'day' ? (
        <DayGrid board={printable} crews={crews} date={date} readOnly />
      ) : view === 'month' ? (
        <MonthGrid
          board={printable}
          month={month}
          weekStart={tenant?.weekStart || 1}
          staffById={new Map((board?.staff || []).map((p) => [p.id, p]))}
          readOnly
        />
      ) : (
        <WeekGrid board={printable} crews={crews} wide={view === '2week'} readOnly />
      )}
    </PrintShell>
  )
}
